class BundleBuilder extends HTMLElement {
    constructor() {
        super();
        this.bundleData = JSON.parse(this.querySelector('#bundle-data').innerHTML).sort((a, b) => a.count - b.count);
        this.selectedBundle = this.bundleData[0];
        this.currencySymbol = this.getAttribute('data-currency-symbol');

        // State of products.
        this.selectedProducts = []; // { id: variant.id, image: image.url, price: variant.price }

        this.initElements();
        this.loadStateFromStorage();
        this.initializeTabStickyListener();
        this.initializeStickyButtonListener();
        this.initializeTabListeners();
        this.updateUI(); // Run before initProductPagination so button states from onProductChange have final say
        this.initProductPagination();
        this.initializeProductCardListeners();
        this.initializeProductSliderListeners();
        this.initializePopupCarousel();
        this.initializeBundleATCListener();

        this.addEventListener('bundle:add', this.handleBundleAdd.bind(this));
        this.addEventListener('bundle:remove', this.handleBundleRemove.bind(this));
    }

    getStorageKey() {
        // Include pathname so different bundle pages don't clash
        return `bundleBuilderState:${window.location.pathname}`;
    }

    saveStateToStorage() {
        try {
            const state = {
                selectedBundleCount: this.selectedBundle?.count,
                selectedProducts: this.selectedProducts || [],
            };
            window.localStorage.setItem(this.getStorageKey(), JSON.stringify(state));
        } catch (e) {
            console.warn('BundleBuilder: unable to save state', e);
        }
    }

    loadStateFromStorage() {
        try {
            const raw = window.localStorage.getItem(this.getStorageKey());
            if (!raw) return;

            const state = JSON.parse(raw);
            if (!state || typeof state !== 'object') return;

            const { selectedBundleCount, selectedProducts } = state;

            // Restore selected bundle if it still exists
            if (selectedBundleCount) {
                const matchedBundle = this.bundleData.find(b => b.count === selectedBundleCount);
                if (matchedBundle) {
                    this.selectedBundle = matchedBundle;
                }
            }

            if (Array.isArray(selectedProducts)) {
                this.selectedProducts = selectedProducts
                    .filter(p => p && p.id && p.image && typeof p.price !== 'undefined');
            }
        } catch (e) {
            console.warn('BundleBuilder: unable to load state', e);
        }
    }

    clearStateFromStorage() {
        try {
            window.localStorage.removeItem(this.getStorageKey());
        } catch (e) {
            console.warn('BundleBuilder: unable to clear state', e);
        }
    }

   formatMoney(amount) {
    return `${this.currencySymbol}${(amount / 100).toFixed(2)}`;
}

    localizeDollarsToCurrency(amount) {
        return amount * window.Shopify.currency.rate;
    }

    initElements() {
        // Tabs
        this.tabsSection = this.querySelector('#tabs-section');
        this.tabs = this.tabsSection.querySelector('.tabs');
        this.tabContents = [...this.querySelectorAll('.tab-content')];
        this.tabButtons = [...this.tabs.querySelectorAll('.tab-btn')];

        // Products
        this.productSliders = this.querySelectorAll('.product-slider');

        // Bundles
        this.bundleOptions = this.querySelector('.bundle-options');
        this.progressBars = this.querySelectorAll('.progress-fill');

        // Main progress section elements
        this.progressSavingsAmount = this.querySelector('.progress-savings-amount');
        this.progressStatus = this.querySelector('.progress-status');

        // Populate bundle options
        this.bundleData.forEach(bundle => {
            const bundleOption = this.querySelector('#bundle-template').content.cloneNode(true).querySelector('div');
            bundleOption.querySelector('.bundle-count').textContent = bundle.count;
            let price = this.formatMoney(this.localizeDollarsToCurrency(bundle.discount * 100));
            price = price.replace(/\s/g, '');
            bundleOption.querySelector('.bundle-discount').textContent = `${price} off`;

            bundleOption.dataset.count = bundle.count;
            this.bundleOptions.appendChild(bundleOption);

            bundleOption.addEventListener('click', () => {
                this.onBundleChange(bundle.count);
            });
        });

        // Price elements
        this.originalPrice = this.querySelector('[data-original-price]');
        this.discountedPrice = this.querySelector('[data-discounted-price]');
        this.discount = this.querySelector('[data-discount]');

        // Bundle ATC button
        this.bundleATCButton = this.querySelector('.btn_cr:not(.sticky-btn)');
        this.stickyButtonContainer = this.querySelector('.sticky-btn-container');
        this.stickyButton = this.stickyButtonContainer?.querySelector('.sticky-btn');

        // Sticky bar progress elements
        this.stickyItemsAdded = this.stickyButtonContainer?.querySelector('.sticky-items-added');
        this.stickyItemsRemaining = this.stickyButtonContainer?.querySelector('.sticky-items-remaining');
        this.stickyProgressFill = this.stickyButtonContainer?.querySelector('.sticky-progress-fill');
        this.stickySavingsAmount = this.stickyButtonContainer?.querySelector('.sticky-savings-amount');
        this.stickyStatus = this.stickyButtonContainer?.querySelector('.sticky-status');
        this.stickyStatusCount = this.stickyButtonContainer?.querySelector('.sticky-status-count');
    }

    initializeBundleATCListener() {
        // this.bundleATCButton.addEventListener('click', () => {
        //     const itemAddStrings = this.selectedProducts.map((product, index) => `items[${index}][id]=${product.id}&items[${index}][quantity]=1`);
        //     const checkoutParamString = itemAddStrings.join('&');
        //     const addToCartUrl = `/cart/add?${checkoutParamString}&return_to=/checkout`;
        //     const checkoutUrl = `/cart/clear?return_to=${encodeURIComponent(addToCartUrl)}`;
        //     window.location.href = checkoutUrl;
        // });
        this.bundleATCButton.addEventListener('click', () => {
            const itemAddStrings = this.selectedProducts.map((product) => `${product.id}:1`);
            const checkoutParamString = `${itemAddStrings.join(',')}?discount=${this.selectedBundle.discount_code}`;
            const permalink = `/cart/${checkoutParamString}`;
            this.clearStateFromStorage();
            window.location.href = permalink;
        });
    }

    updateUI() {
        // Update the progress bars based on the selected bundle and products.
        const progressBarWidth = (this.selectedProducts.length / this.selectedBundle.count) * 100;
        this.progressBars.forEach(pb=>pb.style.width = `${progressBarWidth}%`);

        // Calculate items for status updates
        const itemsAdded = this.selectedProducts.length;
        const itemsRemaining = this.selectedBundle.count - itemsAdded;
        const isBundleComplete = itemsAdded === this.selectedBundle.count;

        // Update main progress section savings and status
        if (this.progressSavingsAmount) {
            const savingsAmount = this.localizeDollarsToCurrency(this.selectedBundle.discount * 100);
            this.progressSavingsAmount.textContent = this.formatMoney(savingsAmount);
        }
        if (this.progressStatus) {
            if (isBundleComplete) {
                this.progressStatus.textContent = 'Bundle complete! Ready to checkout';
                this.progressStatus.classList.add('complete');
            } else {
                const progressPercent = Math.round(progressBarWidth);
                this.progressStatus.innerHTML = `Add <span class="progress-status-count">${itemsRemaining}</span> more to complete your bundle (${progressPercent}% done)`;
                this.progressStatus.classList.remove('complete');
            }
        }

        // Update the placeholders based on the products
        const placeholderImage = this.querySelector('template[data-placeholder-image]').content.cloneNode(true).querySelector('img');
        const imagesContainer = this.querySelector('.curve-images');

        imagesContainer.innerHTML = '';

        // For each product, add the image to the images container.
        this.selectedProducts.forEach(product => {
            const img = document.createElement('img');
            img.src = product.image;

            // Create cross button for the image.
            const crossButton = document.createElement('button');
            crossButton.classList.add('cross-btn');
            crossButton.innerHTML = '×';
            crossButton.addEventListener('click', () => {
                this.dispatchEvent(new CustomEvent('bundle:remove', {
                    detail: { variantId: product.id }
                }));
            });

            const imgContainer = document.createElement('div');
            imgContainer.classList.add('img-container');
            imgContainer.appendChild(img);
            imgContainer.appendChild(crossButton);

            imagesContainer.appendChild(imgContainer);
        });

        // Fill remaining images with placeholders
        for (let i = this.selectedProducts.length; i < this.selectedBundle.count; i++) {
            imagesContainer.appendChild(placeholderImage.cloneNode(true));
        }

        // Update bundle options UI based on selected bundle.
        this.bundleOptions.querySelectorAll('.bundle').forEach(bundle => {
            bundle.classList.remove('active');
        });
        this.bundleOptions.querySelector(`[data-count="${this.selectedBundle.count}"]`).classList.add('active');

        // Update price UI based on selected bundle and products.
        const totalPrice = this.selectedProducts.reduce((acc, product) => acc + +product.price, 0);

        this.discount.textContent = this.formatMoney(this.localizeDollarsToCurrency(this.selectedBundle.discount * 100));

        // Items have not been added yet.
        if (totalPrice === 0) {
            this.originalPrice.textContent = ''
            this.discountedPrice.textContent = ''
        } else {
            this.originalPrice.textContent = this.formatMoney(totalPrice);

            // Bundle discount is in dollars, need to convert to proper currency.
            const bundleDiscountLocalized = this.localizeDollarsToCurrency(this.selectedBundle.discount * 100);

            const discountedPrice = totalPrice - bundleDiscountLocalized;

            this.discountedPrice.textContent = this.formatMoney(discountedPrice);
        }

        // Update bundle ATC button and product add buttons based on bundle fullness
        if (isBundleComplete) {
            // Bundle is full - enable checkout, disable adding more
            this.bundleATCButton.disabled = false;
            this.bundleATCButton.classList.remove('disabled');
            if (this.stickyButton) {
                this.stickyButton.disabled = false;
                this.stickyButton.classList.remove('disabled');
            }
            this.querySelectorAll('.add-btn').forEach(btn => {
                btn.disabled = true;
                btn.classList.add('disabled');
            });
        } else {
            // Bundle has room - disable checkout, enable adding
            this.bundleATCButton.disabled = true;
            this.bundleATCButton.classList.add('disabled');
            if (this.stickyButton) {
                this.stickyButton.disabled = true;
                this.stickyButton.classList.add('disabled');
            }
            // Update each product card's button state based on selected options
            // This ensures buttons showing "Select options" remain disabled
            this.querySelectorAll('.product-card').forEach(productCard => {
                this.onProductChange(productCard);
            });
        }

        // Update sticky bar progress info
        if (this.stickyItemsAdded) {
            this.stickyItemsAdded.textContent = `${itemsAdded} item${itemsAdded !== 1 ? 's' : ''} added`;
        }
        if (this.stickyItemsRemaining) {
            if (isBundleComplete) {
                this.stickyItemsRemaining.textContent = 'Bundle complete!';
                this.stickyItemsRemaining.classList.add('complete');
            } else {
                this.stickyItemsRemaining.textContent = `${itemsRemaining} remaining`;
                this.stickyItemsRemaining.classList.remove('complete');
            }
        }
        if (this.stickyProgressFill) {
            const progressPercent = (itemsAdded / this.selectedBundle.count) * 100;
            this.stickyProgressFill.style.width = `${progressPercent}%`;
        }

        // Update sticky bar savings and status (desktop)
        if (this.stickySavingsAmount) {
            const savingsAmount = this.localizeDollarsToCurrency(this.selectedBundle.discount * 100);
            this.stickySavingsAmount.textContent = this.formatMoney(savingsAmount);
        }
        if (this.stickyStatus) {
            if (isBundleComplete) {
                this.stickyStatus.textContent = 'Bundle complete! Ready to checkout';
                this.stickyStatus.classList.add('complete');
            } else {
                const progressPercent = Math.round((itemsAdded / this.selectedBundle.count) * 100);
                this.stickyStatus.innerHTML = `Add <span class="sticky-status-count">${itemsRemaining}</span> more to complete your bundle (${progressPercent}% done)`;
                this.stickyStatus.classList.remove('complete');
            }
        }

        // Persist current state so it can be restored when user navigates away and back
        this.saveStateToStorage();
    }

    // Update UI after bundle selection changes.
    onBundleChange(count) {
        if (count === this.selectedBundle.count) {
            return;
        }

        this.selectedBundle = this.bundleData.find(bundle => bundle.count === count);

        // If switching to a smaller bundle, remove excess products from the end
        if (this.selectedProducts.length > count) {
            this.selectedProducts = this.selectedProducts.slice(0, count);
        }
        // If switching to a larger bundle, keep all existing products

        this.updateUI();
    }

    handleBundleAdd(e) {
        const { variantId, count, image, price } = e.detail;

        const postTotal = this.selectedProducts.length + count;

        if (postTotal > this.selectedBundle.count) {
            return;
        }

        for (let i = 0; i < count; i++) {
            this.selectedProducts.push({
                id: variantId,
                image: image,
                price: price
            });
        }

        this.updateUI();
    }
    handleBundleRemove(e) {
        const variantId = e.detail.variantId;
        // Remove the last occurrence of this variant id
        let bundleToRemove = -1;
        for (let i = this.selectedProducts.length - 1; i >= 0; i--) {
            if (this.selectedProducts[i].id === variantId) {
                bundleToRemove = i;
                break;
            }
        }

        if (bundleToRemove !== -1) {
            this.selectedProducts.splice(bundleToRemove, 1);
            this.updateUI();
        }
    }

    initializeTabStickyListener() {
        const tabsOffset = this.tabsSection.offsetTop;

        window.addEventListener('scroll', () => {
            if (window.scrollY >= tabsOffset) {
                this.tabs.classList.add('is-sticky');
            } else {
                this.tabs.classList.remove('is-sticky');
            }
        });
    }

    initializeStickyButtonListener() {
        if (!this.bundleATCButton || !this.stickyButtonContainer || !this.stickyButton) return;

        // Sync sticky button state with original button
        const syncStickyButtonState = () => {
            if (this.bundleATCButton.disabled) {
                this.stickyButton.disabled = true;
                this.stickyButton.classList.add('disabled');
            } else {
                this.stickyButton.disabled = false;
                this.stickyButton.classList.remove('disabled');
            }
        };

        // Add click handler to sticky button
        this.stickyButton.addEventListener('click', () => {
            this.bundleATCButton.click();
        });

        // Show/hide sticky button based on scroll position
        window.addEventListener('scroll', () => {
            const buttonRect = this.bundleATCButton.getBoundingClientRect();

            // Show sticky button when original button is scrolled out of view (disappeared above viewport)
            // buttonRect.bottom < 0 means the button is completely above the viewport
            if (buttonRect.bottom < 0) {
                this.stickyButtonContainer.classList.add('mobile-visible');
                syncStickyButtonState();
            } else {
                this.stickyButtonContainer.classList.remove('mobile-visible');
            }
        });

        // Initial sync
        syncStickyButtonState();
    }

    initializeTabListeners() {
        this.tabButtons.forEach(button => {
            button.addEventListener('click', () => {
                /* ACTIVE TAB SWITCH */
                this.querySelectorAll('.tab-btn, .tab-content').forEach((el) => el.classList.remove('active'));
                button.classList.add('active');

                const activeTab = this.querySelector(`#${button.dataset.tab}`);
                activeTab.classList.add('active');


                /* TAB SWITCH FIX */
                /* Reset products for new tab */
                const products = activeTab.querySelectorAll('.product-card');
                const loadBtn = activeTab.querySelector('.loadmore-btn');
                const initialVisibleCount = 8;
                this.toggleProductVisibility(products, initialVisibleCount, loadBtn);

                /* ✅ SCROLL TO TABS TOP - Always scroll up when changing collections */
                const headerOffset = window.innerWidth < 768 ? 80 : 120; // mobile / desktop

                // Get the original position of tabs section in the document
                // Use the tab content area as reference since tabs may be fixed/sticky
                const scrollTarget = activeTab.getBoundingClientRect().top + window.pageYOffset - headerOffset - 60;

                window.scrollTo({
                    top: Math.max(0, scrollTarget),
                    behavior: 'smooth',
                });
            });
        });
    }

    moveSliderTo(slider, index) {
        const track = slider.querySelector('.slider-track');
        track.style.transform = 'translateX(-' + index * 100 + '%)';
    }

    initializeProductSliderListeners() {
        this.productSliders.forEach(slider => {
            const slides = slider.querySelectorAll('.slide');
            const prev = slider.querySelector('.left');
            const next = slider.querySelector('.right');
            if (!slides.length || !prev || !next) return;
            let index = 0;

            next.addEventListener('click', (e) => {
                e.preventDefault();
                index = (index + 1) % slides.length;
                this.moveSliderTo(slider, index);
            });

            prev.addEventListener('click', (e) => {
                e.preventDefault();
                index = (index - 1 + slides.length) % slides.length;
                this.moveSliderTo(slider, index);
            });
        });
    }

    toggleProductVisibility(products, visibleCount, loadBtn) {
        products.forEach((card, index) => {
            if (index < visibleCount) {
                card.classList.add('visible');
            }
        });

        if (visibleCount >= products.length) {
            loadBtn.classList.add('hide');
        }
    }

    initProductPagination() {
        for (const tab of this.tabContents) {
            const products = tab.querySelectorAll('.product-card');

            // Initialize product atc buttons.
            products.forEach(product => {
                this.onProductChange(product);
            });

            const loadBtn = tab.querySelector('.loadmore-btn');
            let visibleCount = 8;

            this.toggleProductVisibility(products, visibleCount, loadBtn);
            loadBtn.addEventListener('click', (e) => {
                e.preventDefault(); // Prevent default anchor behavior (scrolling to top)
                visibleCount += 8;
                this.toggleProductVisibility(products, visibleCount, loadBtn);
            });
        }
    }

    onProductChange(productCard) {
        function updateATCButton(atcBtn, variant) {
            if (variant && variant.available) {
                atcBtn.disabled = false;
                atcBtn.classList.remove('disabled');
                atcBtn.textContent = atcBtn.getAttribute('data-available');
                atcBtn.dataset.id = variant.id;
                atcBtn.dataset.price = variant.price;
            } else {
                atcBtn.disabled = true;
                atcBtn.classList.add('disabled');
                atcBtn.textContent = atcBtn.getAttribute('data-out-of-stock');
                atcBtn.dataset.id = '';
                atcBtn.dataset.price = '';
            }
        }


        const activeColorSwatch = productCard.querySelector('.color-swatch.active');
        const activeSizeSwatch = productCard.querySelector('.size-swatch.active');

        const atcBtn = productCard.querySelector('.add-btn');
        const productData = JSON.parse(productCard.querySelector('script[type="application/json"]').innerHTML);


        // Product has no options, could be single variant.
        if (!activeColorSwatch && !activeSizeSwatch) {
            if (productData.variants.length !== 1) {
                atcBtn.disabled = true;
                atcBtn.classList.add('disabled');
                atcBtn.textContent = 'Select options'
                return;
            } else {
                const variant = productData.variants[0];
                updateATCButton(atcBtn, variant);
                return;
            }

        }

        const colorValue = activeColorSwatch?.dataset?.value || null;
        const colorOptionIndex = activeColorSwatch?.dataset?.optionIndex || '';

        const sizeValue = activeSizeSwatch?.dataset?.value || null;
        const sizeOptionIndex = activeSizeSwatch?.dataset?.optionIndex || '';

        const variant = productData.variants.find(v =>
            (!colorOptionIndex || v[`option${colorOptionIndex}`] === colorValue) &&
            (!sizeOptionIndex || v[`option${sizeOptionIndex}`] === sizeValue)
        );


        const productSlider = productCard.querySelector('.product-slider');

        if (variant) {
            // Update the product card image.
            const productCardImages = [...productCard.querySelectorAll('.popup-img')];
            const variantImageIndex = productCardImages.findIndex(img => img.dataset.variants.includes(variant.id));

            if (variantImageIndex !== -1) {
                this.moveSliderTo(productSlider, variantImageIndex);
            }
        }

        updateATCButton(atcBtn, variant);

    }

    initializeProductCardListeners() {
        this.addEventListener('click', (e) => {
            // Color swatch
            if (e.target.classList.contains('color-swatch')) {
                const group = e.target.closest('.color-swatches');
                group.querySelectorAll('.color-swatch').forEach((s) => s.classList.remove('active'));
                e.target.classList.add('active');
            }

            // Size swatch
            if (e.target.classList.contains('size-swatch')) {
                const group = e.target.closest('.size-swatches');
                group.querySelectorAll('.size-swatch').forEach((s) => s.classList.remove('active'));
                e.target.classList.add('active');
            }

            if (e.target.classList.contains('plus')) {
                const quantity = e.target.closest('.qty').querySelector('input');
                quantity.value = parseInt(quantity.value) + 1;
            }
            if (e.target.classList.contains('minus')) {
                const quantity = e.target.closest('.qty').querySelector('input');
                quantity.value = parseInt(quantity.value) - 1;
            }

            // Check if selected combination is valid/available.
            if (e.target.classList.contains('color-swatch') || e.target.classList.contains('size-swatch')) {
                this.onProductChange(e.target.closest('.product-card'));
            }


            // Add to cart button
            if (e.target.classList.contains('add-btn')) {
                const variantId = e.target.dataset.id;
                const variantPrice = e.target.dataset.price;
                const productCardImages = [...e.target.closest('.product-card').querySelectorAll('.popup-img')];
                const variantImage = productCardImages.find(img => img.dataset.variants.includes(variantId)) || productCardImages[0];

                const quantity = e.target.closest('.product-card').querySelector('input[type="number"]').value;

                if (variantId) {
                    this.dispatchEvent(new CustomEvent('bundle:add', {
                        detail: { variantId: variantId, count: +quantity, image: variantImage.src, price: variantPrice }
                    }));
                }
            }
        });
    }

    initializePopupCarousel(e) {
        let slides = [];
        let current = 0;

        function setupCarouselControls(e) {
            const popup = document.getElementById('global-popup');
            if (!popup || !slides.length) return;

            if (e.target.classList.contains('close-popup')) {
                popup.classList.remove('active');
            }

            if (e.target.classList.contains('next')) {
                slides[current].classList.remove('active');
                current = (current + 1) % slides.length;
                slides[current].classList.add('active');
            }

            if (e.target.classList.contains('prev')) {
                slides[current].classList.remove('active');
                current = (current - 1 + slides.length) % slides.length;
                slides[current].classList.add('active');
            }
        }

        function handlePopupImage(e) {
            const img = e.target.closest('.popup-img');
            if (!img) return;

            const popup = document.getElementById('global-popup');
            const container = popup.querySelector('.popup-images');

            const product = img.closest('.product-card');
            if (!product) return;

            const imgs = product.querySelectorAll('.popup-img');

            container.innerHTML = '';
            slides = [];
            current = 0;

            imgs.forEach((i, index) => {
                const clone = i.cloneNode(true);
                clone.classList.add('popup-slide');

                if (i === img) {
                    clone.classList.add('active');
                    current = index;
                }

                container.appendChild(clone);
                slides.push(clone);
            });

            popup.classList.add('active');
        }

        document.addEventListener('click', (e) => {
            handlePopupImage(e);
            setupCarouselControls(e);
        })
    }
}

customElements.define('bundle-builder', BundleBuilder);